import { Flex, Tabs } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAdminBillingDocumentController_findAllOneTimeInvoices } from '@api-client/generated/AdminBillingDocumentController/findAllOneTimeInvoices';
import { useAdminBillingDocumentController_findOneById } from '@api-client/generated/AdminBillingDocumentController/findOneById';
import { useAdminBillingPeriodController_findAll } from '@api-client/generated/AdminBillingPeriodController/findAll';
import { useAdminBillingPeriodController_findOneById } from '@api-client/generated/AdminBillingPeriodController/findOneById';
import { Schemas } from '@api-client/generated/types';
import { IconPlus } from '@assets';
import { Scrollbar } from '@components';
import {
  BillingList,
  BillingOneTimeInvoiceCard,
  BillingPeriodCard,
  BillingRequirementsAlert,
  ModalBillingAction,
  ModalBillingActionPeriod,
  ModalBillingOneTimeInvoice,
} from '@entities';
import { useCurrentCompany, useCurrentCompanyId } from '@hooks';
import { getMissingInvoicingFields } from '@utils';

import * as S from './styled';

type BillingPeriod = Schemas.BillingPeriod;
type BillingDocument = Schemas.BillingDocument;

type ActionType = 'add' | 'edit';

const Billing = () => {
  const { billingId, oneTimeBillingId } = useParams();
  const companyId = useCurrentCompanyId();
  const { data: company } = useCurrentCompany();

  const [modalActionVisible, setModalActionVisible] = useState(false);
  const [modalOneTimeInvoiceVisible, setModalOneTimeInvoiceVisible] =
    useState(false);
  const [modalActionType, setModalActionType] = useState<ActionType>('add');
  // Index of the service being edited within the period's items array. Items
  // have no reliable id, so edits are targeted by position (see BillingPeriodCard).
  const [selectedInvoiceIndex, setSelectedInvoiceIndex] = useState<
    number | null
  >(null);

  const [modalActionPeriodVisible, setModalActionPeriodVisible] =
    useState(false);
  const [modalActionPeriodType, setModalActionPeriodType] =
    useState<ActionType>('add');

  const [billingPeriodsList, setBillingPeriodsList] = useState<BillingPeriod[]>(
    []
  );
  const [billingOneTimeInvoicesList, setBillingOneTimeInvoicesList] = useState<
    BillingDocument[]
  >([]);

  const { data: periodsList, refetch: refetchBillingPeriods } =
    useAdminBillingPeriodController_findAll({
      params: {
        companyId: companyId!,
      },
    });

  const { data: oneTimeInvoicesList, refetch: refetchBillingOneTimeInvoices } =
    useAdminBillingDocumentController_findAllOneTimeInvoices({
      params: {
        companyId: companyId!,
      },
    });

  useEffect(() => {
    if (periodsList) {
      setBillingPeriodsList(periodsList);
    }
  }, [periodsList]);

  useEffect(() => {
    if (oneTimeInvoicesList) {
      setBillingOneTimeInvoicesList(oneTimeInvoicesList);
    }
  }, [oneTimeInvoicesList]);

  const {
    data: period,
    isFetching: isFetchingBillingPeriod,
    refetch: refetchBillingPeriod,
  } = useAdminBillingPeriodController_findOneById({
    params: {
      companyId: companyId!,
      id: billingId!,
    },
    config: {
      enabled: !!billingId,
      refetchOnWindowFocus: false,
    },
  });

  const {
    data: oneTimeInvoice,
    isFetching: isFetchingBillingOneTimeInvoice,
    refetch: refetchBillingOneTimeInvoice,
  } = useAdminBillingDocumentController_findOneById({
    params: {
      companyId: companyId!,
      id: oneTimeBillingId!,
    },
    config: {
      enabled: !!oneTimeBillingId,
      refetchOnWindowFocus: false,
    },
  });

  // Billing periods and one-time invoices can only be created once the company
  // has all the data the backend requires to issue an invoice.
  const missingInvoicingFields = getMissingInvoicingFields(company);
  const canInvoice = missingInvoicingFields.length === 0;

  // The edited service is derived from its index, so there is no second piece
  // of state to keep in sync (edits target items by position).
  const selectedInvoiceService =
    selectedInvoiceIndex !== null
      ? period?.items[selectedInvoiceIndex] ?? null
      : null;

  const refetchBilling = () => {
    // Refetch the open period (right card) and the periods list (left card) so
    // the left-column service count/total reflects the change too.
    refetchBillingPeriods();
    refetchBillingPeriod();
  };

  const tabs = useMemo(
    () => [
      {
        key: 'monthly',
        label: 'Monthly invoices',
        children: (
          <Scrollbar height="calc(100vh - 272px)">
            <BillingList
              type="period"
              selectedId={billingId || oneTimeBillingId}
              dataSource={billingPeriodsList}
              onAdd={() => handleOpenPeriodModal('add')}
            />
          </Scrollbar>
        ),
      },
      {
        key: 'one-time',
        label: 'One-time invoices',
        children: (
          <Scrollbar height="calc(100vh - 272px)">
            <BillingList
              type="one-time"
              isDisabled={!canInvoice}
              selectedId={billingId || oneTimeBillingId}
              dataSource={billingOneTimeInvoicesList}
              onAdd={() => handleOpenOneTimeInvoiceModal()}
            />
          </Scrollbar>
        ),
      },
    ],
    [
      billingPeriodsList,
      billingOneTimeInvoicesList,
      billingId,
      oneTimeBillingId,
      canInvoice,
    ]
  );

  const handleOpenModal = (type: ActionType, index: number | null = null) => {
    setModalActionVisible(true);
    setModalActionType(type);
    setSelectedInvoiceIndex(index);
  };

  const handleCloseModal = () => {
    setModalActionVisible(false);
    setModalActionType('add');
    setSelectedInvoiceIndex(null);
  };

  const handleOpenPeriodModal = (type: ActionType) => {
    setModalActionPeriodVisible(true);
    setModalActionPeriodType(type);
  };

  const handleClosePeriodModal = () => {
    setModalActionPeriodVisible(false);
    setModalActionPeriodType('add');
  };

  const handleOpenOneTimeInvoiceModal = () => {
    setModalOneTimeInvoiceVisible(true);
  };

  return (
    <Flex gap={24} vertical>
      <BillingRequirementsAlert
        missingFields={missingInvoicingFields}
        message="Add period and one-time invoices are blocked until the following invoicing details are filled in on the Company Info tab:"
      />

      <ModalBillingAction
        details={period}
        selected={selectedInvoiceService}
        selectedIndex={selectedInvoiceIndex}
        type={modalActionType}
        open={modalActionVisible}
        onAfterUpdate={refetchBilling}
        onCancel={handleCloseModal}
      />

      <ModalBillingActionPeriod
        details={period}
        type={modalActionPeriodType}
        open={modalActionPeriodVisible}
        onAfterCreate={refetchBillingPeriods}
        onAfterUpdate={() => refetchBillingPeriods()}
        onCancel={handleClosePeriodModal}
      />

      <ModalBillingOneTimeInvoice
        isOpen={modalOneTimeInvoiceVisible}
        onAfterCreate={refetchBillingOneTimeInvoices}
        onCancel={() => setModalOneTimeInvoiceVisible(false)}
      />

      <Flex align="flex-start" gap={30}>
        <S.Inner>
          <S.Header align="center" justify="space-between">
            <S.Title>Billing</S.Title>

            <Flex gap={4} vertical>
              <S.AddButton
                icon={<IconPlus width={24} />}
                type="text"
                disabled={!canInvoice}
                onClick={() => handleOpenPeriodModal('add')}
              >
                Add period
              </S.AddButton>

              <S.AddButton
                icon={<IconPlus width={24} />}
                type="text"
                disabled={!canInvoice}
                onClick={() => handleOpenOneTimeInvoiceModal()}
              >
                Add one-time invoice
              </S.AddButton>
            </Flex>
          </S.Header>

          <Tabs items={tabs} />
        </S.Inner>

        <S.Details>
          {period && (
            <BillingPeriodCard
              details={period}
              isLoading={isFetchingBillingPeriod}
              onAdd={() => handleOpenModal('add')}
              onEdit={() => handleOpenPeriodModal('edit')}
              onEditService={(_item, index) => handleOpenModal('edit', index)}
              onAfterUpdate={refetchBilling}
              onAfterCreateDocument={refetchBilling}
            />
          )}
          {oneTimeInvoice && (
            <BillingOneTimeInvoiceCard
              details={oneTimeInvoice}
              isLoading={isFetchingBillingOneTimeInvoice}
              onAfterUpdate={() => refetchBillingOneTimeInvoice()}
            />
          )}
        </S.Details>
      </Flex>
    </Flex>
  );
};

export default Billing;
